import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Zap, ExternalLink, Users2, Building2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { VIEWER_URL } from '../lib/appUrls';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

interface CollaboratorWork {
  work_id: string;
  work_title: string;
  work_slug: string | null;
  owner_name: string;
  split_percentage: number;
  role_label: string;
  slices_earned: number;
  contract_id: string;
}

export default function Collaborations() {
  const [works, setWorks] = useState<CollaboratorWork[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollaborations();
  }, []);

  const fetchCollaborations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_collaborator_works');
      if (error) throw error;
      setWorks((data || []) as CollaboratorWork[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load collaborations');
    } finally {
      setLoading(false);
    }
  };

  const totalSlices = works.reduce((sum, w) => sum + Number(w.slices_earned || 0), 0);

  if (loading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-3xl font-bold'>Collaborations</h1>
          <p className='text-muted-foreground mt-1 text-sm'>Works you appear in as a revenue collaborator</p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {[1, 2].map(i => (
            <Card key={i} className='partner-card p-6'>
              <Skeleton className='h-4 w-24 mb-3' />
              <Skeleton className='h-8 w-32' />
            </Card>
          ))}
        </div>
        <Card className='partner-card p-6'>
          <Skeleton className='h-6 w-40 mb-4' />
          <Skeleton className='h-20 w-full' />
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Collaborations</h1>
        <p className='text-muted-foreground mt-1 text-sm'>
          Works where you have a revenue split, owned by another studio — read-only view.
        </p>
      </div>

      {/* Summary */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Card className='partner-card p-6'>
          <div className='flex items-center gap-3'>
            <Users2 className='h-8 w-8 text-primary flex-shrink-0' />
            <div>
              <p className='text-sm text-muted-foreground'>Collaborative Works</p>
              <p className='text-2xl font-semibold'>{works.length}</p>
            </div>
          </div>
        </Card>
        <Card className='partner-card p-6'>
          <div className='flex items-center gap-3'>
            <Zap className='h-8 w-8 text-primary flex-shrink-0' />
            <div>
              <p className='text-sm text-muted-foreground'>Total Slices Earned</p>
              <p className='text-2xl font-semibold'>{totalSlices.toLocaleString()}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Works list */}
      <Card className='partner-card'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base'>Your Collaborative Works</CardTitle>
          <CardDescription>
            Slice earnings reflect your percentage of total donations received. Splits applied at payout.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {works.length === 0 ? (
            <div className='text-center py-12 space-y-3'>
              <Users2 className='h-12 w-12 mx-auto text-muted-foreground opacity-30' />
              <p className='text-sm font-medium text-muted-foreground'>No collaborations yet</p>
              <p className='text-xs text-muted-foreground'>
                When another studio adds you as a collaborator on their work, it will appear here.
              </p>
            </div>
          ) : (
            <div className='space-y-3'>
              {works.map(work => (
                <div
                  key={work.contract_id}
                  className='flex items-center justify-between gap-4 p-4 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors'
                >
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <p className='text-sm font-semibold truncate'>{work.work_title}</p>
                      <Badge variant='outline' className='text-xs'>
                        {work.role_label}
                      </Badge>
                    </div>
                    <div className='flex items-center gap-2 mt-1 text-xs text-muted-foreground'>
                      <Building2 className='h-3 w-3 flex-shrink-0' />
                      <span className='truncate'>{work.owner_name}</span>
                    </div>
                  </div>
                  <div className='flex items-center gap-4 shrink-0'>
                    <div className='text-right'>
                      <p className='text-xs text-muted-foreground'>Your share</p>
                      <p className='text-sm font-bold text-primary'>{work.split_percentage}%</p>
                    </div>
                    <div className='text-right'>
                      <p className='text-xs text-muted-foreground'>Slices earned</p>
                      <div className='flex items-center gap-1 justify-end'>
                        <Zap className='h-3 w-3 text-primary' />
                        <p className='text-sm font-semibold'>{Number(work.slices_earned).toLocaleString()}</p>
                      </div>
                    </div>
                    {work.work_slug && (
                      <Button variant='ghost' size='icon' className='h-8 w-8 text-muted-foreground' asChild>
                        <a
                          href={`${VIEWER_URL}/watch/${work.work_slug}`}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          <ExternalLink className='h-3.5 w-3.5' />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <p className='text-xs text-muted-foreground text-center'>
        This view is read-only. Contact the owning studio to update your split percentage or role. Splits
        applied at payout — no real-time distribution occurs.
      </p>
    </div>
  );
}
